import React, {useEffect, useMemo, useState} from 'react'
import {Helmet} from 'react-helmet-async'
import {Link} from 'react-router-dom'
import {Button} from '../../../components/library/buttons'
import {Copyable} from '../../../components/library/copy'
import {TextInput} from '../../../components/library/form'
import {Icon} from '../../../components/library/icon/icon'
import {
  List,
  ListColumns,
  NoResultsPermissions
} from '../../../components/library/list'
import {LegacyFormModal} from '../../../components/modals'
import {
  StreamDetailFragment,
  useStreamListLazyQuery
} from '../../../data/workbench-api/generated/stream'
import {
  PermissionAction,
  PermissionResource
} from '../../../data/workbench-api/generated/types'
import {
  ADD_STREAM,
  EDIT_STREAM,
  sortMandatoryThenAlpha
} from '../../../data/workbench-api/stream'
import {EnvironmentNode} from '../../../environment'
import {useAnalytics} from '../../../lib/analytics/analytics'
import {
  IconHeading,
  Mono,
  Section,
  SectionContent,
  SectionInfo
} from '../components'
import {Overflow} from './overflow'

const streamIsValid = (obj: Partial<StreamDetailFragment>) =>
  !!obj.name && obj.name !== 'All cards' // try to reserve this name

type CurrentModal =
  | {
      type: 'new'
    }
  | {
      type: 'name'
      stream: StreamDetailFragment
    }

export const Streams = ({environment}: {environment: EnvironmentNode}) => {
  const [query, {data: streamData, refetch = () => null, loading}] =
    useStreamListLazyQuery({
      variables: {
        environmentId: environment.id
      }
    })
  const streams = streamData?.streams?.nodes
    ?.map(stream => ({
      ...stream,
      hasOverflow: !stream.isMandatory
    }))
    ?.sort(sortMandatoryThenAlpha)

  const [currentModal, setCurrentModal] = useState<CurrentModal | null>(null)

  const canEdit = environment.hasPermission(
    PermissionResource.Stream,
    PermissionAction.Edit
  )

  const canView = environment.hasPermission(
    PermissionResource.Stream,
    PermissionAction.View
  )

  useEffect(() => {
    if (canView) {
      query()
    }
  }, [canView, query])

  const analytics = useAnalytics()

  useEffect(() => {
    analytics.track({
      event: 'Viewed sdk streams configuration',
      properties: {
        org_id: environment.organisationId,
        environment_id: environment.id,
        category: 'configuration>sdk>streams'
      }
    })
  }, [analytics, environment])

  const streamColumns = useMemo(
    (): ListColumns<StreamDetailFragment> => [
      {
        id: 'name',
        header: 'Name',
        accessor: r =>
          canEdit && !r.isMandatory ? (
            <a
              onClick={() => {
                setCurrentModal({type: 'name', stream: r})
              }}
              style={{marginRight: '-10px'}}
            >
              {r.name}
            </a>
          ) : (
            <strong style={{marginRight: '10px'}}>{r.name}</strong>
          )
      },
      {
        id: 'id',
        header: 'ID',
        accessor: r => (
          <>
            <Mono style={{marginRight: '5px'}}>{r.id}</Mono>
            <Copyable style={{width: '70px'}} value={r.id} />
          </>
        )
      },
      {
        id: 'containers',
        header: 'Connected containers',
        accessor: r => (
          <Link to="../stream-containers">{r.containers.totalCount}</Link>
        )
      }
    ],
    [canEdit]
  )

  return (
    <Section>
      <Helmet>
        <title>Streams</title>
      </Helmet>
      <header>
        <IconHeading>
          <Icon name="stream" /> Streams
        </IconHeading>

        <nav>
          <Button
            disabled={!canEdit}
            onClick={() => setCurrentModal({type: 'new'})}
          >
            New stream
          </Button>
        </nav>
      </header>

      <SectionInfo>
        <p>
          Create collections of cards which can be assigned to different
          containers in your apps
        </p>
      </SectionInfo>

      <SectionContent>
        <List<StreamDetailFragment>
          columns={streamColumns}
          rows={streams ?? []}
          loading={loading}
          bleed={'#f2f2f2'}
          renderOverflow={(stream, closeDropdown) => (
            <Overflow
              canEdit={canEdit}
              stream={stream}
              closeDropdown={closeDropdown}
              refetch={refetch}
              edit={() => setCurrentModal({type: 'name', stream})}
              environment={environment}
            />
          )}
          renderEmpty={canView ? undefined : () => <NoResultsPermissions />}
        />
      </SectionContent>
      {currentModal && currentModal.type === 'name' ? (
        <LegacyFormModal<StreamDetailFragment>
          obj={currentModal.stream}
          handleClose={() => setCurrentModal(null)}
          title={'Edit ' + currentModal.stream.name}
          mutation={EDIT_STREAM}
          isValid={streamIsValid}
          update={() => {
            analytics.track({
              event: 'Edited configuration',
              properties: {
                org_id: environment.organisationId,
                environment_id: environment.id,
                category: 'configuration>sdk>streams'
              }
            })
          }}
        >
          {(stream, updateStream) => {
            return (
              <TextInput
                style={{maxWidth: '100%'}}
                upLabel={'Stream name'}
                label={'Add stream name...'}
                value={stream.name}
                onChange={name => updateStream({name})}
              />
            )
          }}
        </LegacyFormModal>
      ) : null}

      {currentModal && currentModal.type === 'new' ? (
        <LegacyFormModal<{
          environmentId: string
          name: string
        }>
          obj={{
            environmentId: environment.id,
            name: ''
          }}
          isValid={streamIsValid}
          handleClose={() => setCurrentModal(null)}
          title="Add new stream"
          submitText="Add"
          mutation={ADD_STREAM}
          update={() => {
            refetch()
            analytics.track({
              event: 'Added configuration',
              properties: {
                org_id: environment.organisationId,
                environment_id: environment.id,
                category: 'configuration>sdk>streams'
              }
            })
          }}
        >
          {(stream, updateStream) => {
            return (
              <>
                <TextInput
                  style={{maxWidth: '100%'}}
                  label={'Add stream name...'}
                  upLabel={'Stream name'}
                  value={stream.name}
                  onChange={name => updateStream({name})}
                />
              </>
            )
          }}
        </LegacyFormModal>
      ) : null}
    </Section>
  )
}
